/**
 * Single row in the Rules desktop table.
 *
 * Renders one Rule across all currently visible columns. Column
 * order and visibility are driven by the Customize drawer via
 * `visibleColumns` passed down from RulesTable. The checkbox cell
 * is rendered as a fixed first column (always visible).
 *
 * Paused rules are dimmed via opacity. Hover highlights the row
 * for scannability. Toggle switches update the parent hook state
 * optimistically; backend swap uses the same event handlers.
 *
 * The three-dot button exposes an anchor element to the parent
 * via `onOpenMenu(anchorEl)`. The parent (page composer) renders
 * the RowActionsMenu as a Portal anchored to that element, so a
 * single menu overlay serves all rows and avoids z-index battles
 * with the sticky header.
 */

import { useRef, type CSSProperties, type RefObject } from "react";
import type { Rule, RuleColumn, RuleColumnId } from "../rulesTypes";
import {
  describeCondition,
  describeSettings,
  formatLastApplied,
  getAppliedToDotColor,
  getAppliedToLabel,
} from "../rulesUtils";
import { CheckboxCell } from "./RulesTable";

const PALETTE = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  line: "#D8DEE6",
  lineSoft: "#E7EAF0",
  bgFade: "#F3F5F5",
};

interface RuleRowProps {
  rule: Rule;
  visibleColumns: RuleColumn[];
  rowDensity: "comfortable" | "compact";
  isSelected: boolean;
  onSelectChange: (selected: boolean) => void;
  onToggleStatus: (next: boolean) => void;
  onToggleAutoPost: (next: boolean) => void;
  onOpenMenu: (anchorEl: HTMLElement) => void;
}

export function RuleRow({
  rule,
  visibleColumns,
  rowDensity,
  isSelected,
  onSelectChange,
  onToggleStatus,
  onToggleAutoPost,
  onOpenMenu,
}: RuleRowProps) {
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const paused = rule.status === "paused";

  const rowStyle: CSSProperties = {
    background: isSelected ? "#F7F9FB" : "transparent",
    opacity: paused ? 0.55 : 1,
    transition: "background 0.1s",
  };

  const cellPadding = rowDensity === "compact" ? "8px 14px" : "14px 14px";

  const handleMenuClick = () => {
    if (menuBtnRef.current) onOpenMenu(menuBtnRef.current);
  };

  return (
    <tr style={rowStyle}>
      <td style={{ ...cellStyle, padding: cellPadding, width: 36 }}>
        <CheckboxCell
          checked={isSelected}
          onChange={onSelectChange}
          ariaLabel={`Select rule ${rule.name}`}
        />
      </td>
      {visibleColumns.map((column) => (
        <td key={column.id} style={{ ...cellStyle, padding: cellPadding }}>
          {renderCell(column.id, rule, {
            onToggleStatus,
            onToggleAutoPost,
            onMenuClick: handleMenuClick,
            menuBtnRef,
          })}
        </td>
      ))}
    </tr>
  );
}

interface CellRenderCtx {
  onToggleStatus: (next: boolean) => void;
  onToggleAutoPost: (next: boolean) => void;
  onMenuClick: () => void;
  menuBtnRef: RefObject<HTMLButtonElement | null>;
}

function renderCell(columnId: RuleColumnId, rule: Rule, ctx: CellRenderCtx) {
  switch (columnId) {
    case "priority":
      return <PriorityCell priority={rule.priority} />;
    case "name":
      return <NameCell rule={rule} />;
    case "appliedTo":
      return <AppliedToCell rule={rule} />;
    case "conditions":
      return <ConditionsCell rule={rule} />;
    case "settings":
      return <SettingsCell rule={rule} />;
    case "autoPost":
      return (
        <Toggle
          checked={rule.autoPost}
          onChange={ctx.onToggleAutoPost}
          ariaLabel={`Auto-post for ${rule.name}`}
        />
      );
    case "status":
      return (
        <Toggle
          checked={rule.status === "active"}
          onChange={(next) => ctx.onToggleStatus(next)}
          ariaLabel={`Status for ${rule.name}`}
        />
      );
    case "actions":
      return (
        <button
          ref={ctx.menuBtnRef}
          type="button"
          aria-label={`Actions for ${rule.name}`}
          onClick={ctx.onMenuClick}
          style={menuBtnStyle}
        >
          ⋯
        </button>
      );
  }
}

function PriorityCell({ priority }: { priority: number }) {
  return <span style={priorityStyle}>{priority}</span>;
}

function NameCell({ rule }: { rule: Rule }) {
  const paused = rule.status === "paused";
  const subText = paused
    ? `Paused · applied ${rule.matchCount} times total`
    : `Applied ${rule.matchCount} ${rule.matchCount === 1 ? "time" : "times"} · ${formatLastApplied(rule.lastAppliedAt)}`;

  return (
    <div>
      <div style={nameStyle}>{rule.name}</div>
      <div style={nameSubStyle}>{subText}</div>
    </div>
  );
}

function AppliedToCell({ rule }: { rule: Rule }) {
  return (
    <span style={appliedChipStyle}>
      <span
        style={{
          ...appliedDotStyle,
          background: getAppliedToDotColor(rule),
        }}
      />
      {getAppliedToLabel(rule)}
    </span>
  );
}

function ConditionsCell({ rule }: { rule: Rule }) {
  return (
    <div>
      {rule.conditions.map((cond, idx) => {
        const parts = describeCondition(cond);
        return (
          <div key={cond.id}>
            {idx > 0 && (
              <span style={condJoinStyle}>{rule.conditionJoin}</span>
            )}
            <div style={condLineStyle}>
              <span style={condKeyStyle}>{parts.keyLabel}</span>
              <span style={condOpStyle}>{parts.operatorLabel}</span>
              <span style={condValStyle}>{parts.valueLabel}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SettingsCell({ rule }: { rule: Rule }) {
  const labels = describeSettings(rule);
  if (labels.length === 0) {
    return <span style={{ fontSize: 11.5, color: PALETTE.ink4 }}>None</span>;
  }
  return (
    <div>
      {labels.map((label, idx) => (
        <span key={idx} style={settingsChipStyle}>
          {label}
        </span>
      ))}
    </div>
  );
}

interface ToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  ariaLabel: string;
}

function Toggle({ checked, onChange, ariaLabel }: ToggleProps) {
  const sliderStyle: CSSProperties = {
    position: "absolute",
    inset: 0,
    background: checked ? PALETTE.ink : PALETTE.line,
    borderRadius: 10,
    transition: "background 0.2s",
    cursor: "pointer",
  };

  const knobStyle: CSSProperties = {
    position: "absolute",
    content: '""',
    height: 16,
    width: 16,
    left: 2,
    top: 2,
    background: "#ffffff",
    borderRadius: "50%",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.18)",
    transition: "transform 0.2s",
    transform: checked ? "translateX(14px)" : "translateX(0)",
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      style={toggleStyle}
    >
      <span style={sliderStyle}>
        <span style={knobStyle} />
      </span>
    </button>
  );
}

const cellStyle: CSSProperties = {
  borderBottom: `1px solid ${PALETTE.lineSoft}`,
  fontSize: 13,
  color: PALETTE.ink,
  verticalAlign: "middle",
};

const priorityStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 26,
  height: 26,
  padding: "0 7px",
  fontSize: 12,
  fontWeight: 700,
  color: PALETTE.ink,
  background: PALETTE.bgFade,
  borderRadius: 4,
};

const nameStyle: CSSProperties = {
  fontWeight: 600,
  color: PALETTE.ink,
  fontSize: 13,
};

const nameSubStyle: CSSProperties = {
  fontSize: 11.5,
  color: PALETTE.ink4,
  fontWeight: 500,
  marginTop: 2,
};

const appliedChipStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "3px 9px",
  background: PALETTE.bgFade,
  borderRadius: 4,
  fontSize: 11.5,
  fontWeight: 500,
  color: PALETTE.ink2,
};

const appliedDotStyle: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: "50%",
};

const condLineStyle: CSSProperties = {
  fontSize: 12,
  color: PALETTE.ink2,
  lineHeight: 1.6,
};

const condKeyStyle: CSSProperties = {
  color: PALETTE.ink4,
  fontWeight: 500,
};

const condOpStyle: CSSProperties = {
  color: PALETTE.ink4,
  fontWeight: 500,
  margin: "0 3px",
};

const condValStyle: CSSProperties = {
  color: PALETTE.ink,
  fontWeight: 600,
  fontFamily: "ui-monospace, Menlo, monospace",
  fontSize: 11.5,
  background: PALETTE.bgFade,
  padding: "1px 5px",
  borderRadius: 3,
};

const condJoinStyle: CSSProperties = {
  color: PALETTE.ink4,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.08em",
  margin: "4px 0",
  display: "block",
};

const settingsChipStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "3px 8px",
  fontSize: 11.5,
  fontWeight: 500,
  color: PALETTE.ink2,
  border: `1px solid ${PALETTE.lineSoft}`,
  borderRadius: 5,
  background: "#ffffff",
  margin: "2px 3px 2px 0",
  whiteSpace: "nowrap",
};

const toggleStyle: CSSProperties = {
  position: "relative",
  display: "inline-block",
  width: 34,
  height: 20,
  background: "transparent",
  border: 0,
  padding: 0,
  cursor: "pointer",
};

const menuBtnStyle: CSSProperties = {
  background: "transparent",
  border: 0,
  color: PALETTE.ink3,
  fontSize: 18,
  padding: "4px 8px",
  cursor: "pointer",
  borderRadius: 6,
  lineHeight: 1,
};